import React, { Fragment, useEffect, useState } from "react";
import { bool, func } from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import get from "lodash/get";
import { Link, SocialShare } from "@quintype/components";

import { AppLogo, Button, HeaderMenuItem, Headline } from "../../../atoms";
import { BUTTONS_DATA, STATIC_KEYWORDS } from "../../../../constants";
import { MEMBER_UPDATED, OPEN_HAMBURGER_MENU } from "../../../store/actions";
import { MenuWithDropdown } from "../../../molecules";
import { NavbarSearch } from "../navbar-search";
import { getAiFaqStoryId, useAiFaqData } from "../../../customHooks/useAiFaqData";
import { useSummaryCheck } from "../../../customHooks/useSummaryCheck";
import HamburgerMenu from "../../../atoms/hamburger-menu";
import ThreeDotIcon from "../../../atoms/ThreeDotsIcon";
import SummaryCard from "../../../molecules/SummaryCard";
import { SocialShareTemplate } from "../../../../arrow";
import { useDeviceType } from "../../../customHooks/useDeviceType";
import AuthButton from "../../../atoms/AuthButton";
import { addDatalayerEventCollection, getAiSummaryEligibility, getCookie, hasShowAiSummaryFalse, isSummaryBlockedByOwner } from "../../../utils";
import { getCategoryAndSubCategory, getTemplateName } from "../../../../ga-utils";
import ToastMessage from "../toastMessage";
import { RightArrow } from "../../../../arrow/components/Svgs/right-arrow";
import OneTapLogin from "../../../atoms/OneTapLogin";

import "./top-bar.m.css";
import BookIcon from "../../../atoms/BookIcon";

const getStoryContentId = (story = {}) => story["story-content-id"] || story.id;
const STORY_TOP_MENU_THRESHOLD = 120;

const TopBar = ({ showOneTap, onMenuToggle = false }) => {
  const [toastMessage, setToastMessage] = useState("");
  const [memberUpdated, setMemberUpdated] = useState(false);
  // const [loadOneTap, setGoogleOneTap] = useState(false);
  const isMobile = useDeviceType() !== "desktop";
  const dispatch = useDispatch();
  const { SIGN_IN, GO_AD_FREE } = STATIC_KEYWORDS;
  const isAdfree = useSelector((state) => get(state, ["qt", "config", "subscriber"], false));
  const enableSubscription = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "enable_subscription"], true)
  );

  const member = useSelector((state) => get(state, ["member"], null));
  const isHamburgerMenuOpen = useSelector((state) => get(state, ["isHamburgerMenuOpen"], false));
  const hamburgerMenu = useSelector((state) => get(state, ["qt", "data", "navigationMenu", "hamburgerMenu"], []));
  // const GoogleClientId = useSelector((state) =>
  //   get(state, ["qt", "config", "publisher-attributes", "google_client_id"], "")
  // );
  const [logoClick, setLogoClick] = useState(false);

  const displayOverlay = isHamburgerMenuOpen ? "flex" : "none";
  const toggleHandler = () => {
    dispatch({
      type: OPEN_HAMBURGER_MENU,
      isHamburgerMenuOpen: !isHamburgerMenuOpen,
    });
  };

  const setMember = (member) => {
    console.log("one tap login member updated");
    dispatch({ type: MEMBER_UPDATED, member });
    setMemberUpdated(true);
  };

  const allStates = useSelector((state) => state);
  const [showSubscribeButton, setShowSubscribeButton] = useState(false);
  const qtState = get(allStates, ["qt"], {});
  const config = get(qtState, ["config"]);
  const collection = get(qtState, ["data", "collection"], {});
  const pageType = qtState.pageType;
  const initialStory = get(qtState, ["data", "story"], {});
  const [activeStory, setActiveStory] = useState(null);
  const [isActivePerpetualStory, setIsActivePerpetualStory] = useState(false);
  const [hasLoadedPerpetualStories, setHasLoadedPerpetualStories] = useState(false);
  const [isNearStoryTop, setIsNearStoryTop] = useState(true);
  const story = pageType === "story-page" ? activeStory || initialStory : initialStory;
  const { horizontalDefault } = get(qtState, ["data", "navigationMenu"], {});

  const storyHeaderInView = get(allStates, "storyHeaderInView", true);
  const shouldForceStickyHeadline =
    pageType === "story-page" &&
    (isActivePerpetualStory || (hasLoadedPerpetualStories && !isNearStoryTop));
  const shouldUseStickyHeader = !storyHeaderInView || shouldForceStickyHeadline;
  const iconSize = { width: "18px", height: "18px" };
  const { summaryPresent, showSummary, setShowSummary, summaryIndex } = useSummaryCheck(story?.cards);
  const storyTemplate = get(story, ["story-template"], "");
  const faqUrl = get(config, ["publisher-attributes", "publisher-faq-url"], false);
  const publisherAttributes = get(config, ["publisher-attributes"], {});
  const isInhouseAiSummaryEnabled =
    get(config, ["publisher-attributes", "publisher-inhouse-ai-summary"], false) === true;
  const isSummaryBlockedByStoryAttribute =
    isInhouseAiSummaryEnabled && (hasShowAiSummaryFalse(story) || isSummaryBlockedByOwner(story, publisherAttributes));
  const storyId = getAiFaqStoryId(story);
  const {
    isTextStory,
    isMagazineStory,
    isSummaryEligibleStory,
    canUseApiSummaryFallback,
    shouldEnableAiHubForTextStory,
  } = getAiSummaryEligibility({
    story,
    publisherConfig: publisherAttributes,
    summaryPresent,
  });
  const {
    hasSummary,
    summary,
    headline: apiHeadline,
    heroImageS3Key: apiHeroImageS3Key,
    isLoading,
  } = useAiFaqData({
    faqUrl,
    storyId,
    enabled:
      pageType === "story-page" &&
      isSummaryEligibleStory &&
      faqUrl !== false &&
      !summaryPresent &&
      isInhouseAiSummaryEnabled &&
      !isSummaryBlockedByStoryAttribute &&
      !shouldEnableAiHubForTextStory,
  });
  const hasApiSummary = hasSummary && Boolean(summary);
  const shouldUseApiSummary = canUseApiSummaryFallback && hasApiSummary;
  const shouldShowSummary = !isSummaryBlockedByStoryAttribute && (summaryPresent || shouldUseApiSummary);
  const isSummaryButtonPending = !summaryPresent && canUseApiSummaryFallback && isLoading;
  const shouldRenderSummaryButton =
    pageType === "story-page" &&
    (isMagazineStory || (isTextStory && !shouldEnableAiHubForTextStory)) &&
    !isSummaryBlockedByStoryAttribute &&
    (summaryPresent || shouldUseApiSummary || isSummaryButtonPending);
  const isSummaryButtonEnabled = shouldShowSummary;
  const [shareURL, setShareURL] = useState("");
  const readEmagazineLink = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "read_emagazine_link"], "")
  );
  const isSubscriptionPage = pageType === "subscription-page";
  const {
    show,
    text: magazineBtnText,
    link: magazineBtnLink,
  } = useSelector((state) => get(state, ["qt", "config", "publisher-attributes", "nav_magazine_meta"], ""));

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const toastMessage = urlParams.get("toastMessage") || "";
    if (toastMessage) {
      setToastMessage(toastMessage);
    }
  }, []);

  useEffect(() => {
    if (pageType !== "story-page") return undefined;

    const handleActiveStoryChange = (event) => {
      if (event?.detail?.story) {
        setActiveStory(event.detail.story);
        setIsActivePerpetualStory(event.detail.isPerpetualStory === true);
      }
    };

    window.addEventListener("perpetual-story-active", handleActiveStoryChange);

    return () => {
      window.removeEventListener("perpetual-story-active", handleActiveStoryChange);
    };
  }, [pageType]);

  useEffect(() => {
    if (pageType !== "story-page") return undefined;

    const handlePerpetualStoriesLoaded = () => {
      setHasLoadedPerpetualStories(true);
    };

    window.addEventListener("perpetual-stories-loaded", handlePerpetualStoriesLoaded);

    return () => {
      window.removeEventListener("perpetual-stories-loaded", handlePerpetualStoriesLoaded);
    };
  }, [pageType]);

  useEffect(() => {
    if (pageType !== "story-page") return undefined;

    const updateNearStoryTop = () => {
      const nextIsNearStoryTop = window.scrollY <= STORY_TOP_MENU_THRESHOLD;
      setIsNearStoryTop((currentIsNearStoryTop) =>
        currentIsNearStoryTop === nextIsNearStoryTop ? currentIsNearStoryTop : nextIsNearStoryTop
      );
    };

    updateNearStoryTop();
    window.addEventListener("scroll", updateNearStoryTop, { passive: true });

    return () => {
      window.removeEventListener("scroll", updateNearStoryTop);
    };
  }, [pageType]);

  useEffect(() => {
    setActiveStory(null);
    setIsActivePerpetualStory(false);
    setHasLoadedPerpetualStories(false);
    setIsNearStoryTop(true);
  }, [getStoryContentId(initialStory)]);

  useEffect(() => {
    if (toastMessage && member) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "dl_login_success",
        login_status: "logged-in",
        user_id: `${member?.id}`,
        subscription_status: "not-subscribed",
      });
    }
  }, [member, toastMessage]);

  useEffect(() => {
    if (!isAdfree) {
      document.getElementById("header")?.classList.toggle("sticky-header-top", shouldUseStickyHeader);
    }
    setShareURL(window.location.href);
  }, [isAdfree, shouldUseStickyHeader]);

  useEffect(() => {
    return () => {
      !isAdfree && document.getElementById("header").classList.remove("different-topbar-top");
      !isAdfree && document.getElementById("header").classList.remove("sticky-header-top");
    };
  }, [isAdfree]);

  useEffect(() => {
    document.body.style.overflow = showSummary ? "hidden" : "unset";
  }, [showSummary]);

  useEffect(() => {
    if (isMobile && showSubscribeButton) {
      const handleScroll = () => {
        setShowSubscribeButton(false);
      };

      // Add scroll event listener
      window.addEventListener("scroll", handleScroll, { passive: true });

      // Cleanup function
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [showSubscribeButton, isMobile]);

  useEffect(() => {
    if ((pageType === "section-page" || pageType === "collection-page" || pageType === "home-page") && config) {
      addDatalayerEventCollection(collection, config);
    }
  }, [collection]);

  const toggleThreeDotMenu = () => {
    setShowSubscribeButton(!showSubscribeButton);
  };
  useEffect(() => {
    const qtAuthCookie = getCookie("qt-auth");
    if (logoClick && member && qtAuthCookie) {
      window.location.href = window.location.origin + "/";
    }
  }, [logoClick]);

  useEffect(() => {
    if (memberUpdated) {
      setMemberUpdated(false);
      window.location.reload(window.location.href);
    }
  }, [memberUpdated]);

  const handleLogoClick = () => {
    setLogoClick(true);
    if (typeof window !== "undefined") {
      const categoryInfo = getCategoryAndSubCategory(window.location, pageType, config, story);
      const templateName = getTemplateName(pageType === "story-page" ? story?.["story-template"] : pageType);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "dl_menu_click",
        link_url: "/",
        link_text: "logo",
        link_category: "logo",
        category: categoryInfo.category,
        sub_category: categoryInfo.subCategory,
        content_id: categoryInfo.contentId,
        template_name: templateName,
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };
  const trackPopupSocialShareClick = (platform) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType, config, story);

      let templateName;
      if (story?.["story-template"]) {
        templateName = getTemplateName(story?.["story-template"]);
      } else {
        templateName = getTemplateName(pageType);
      }
      window.dataLayer.push({
        event: "dl_story_interactions",
        event_action: "share_click",
        link_text: platform || "share",
        category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: templateName,
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  const headlineOrMenuItems =
    pageType === "story-page" && shouldUseStickyHeader ? (
      <Headline type={2} customStyleName="ellipsis secondary-header-headline" customClassName="sticky-nav-header-title">
        {story?.headline || collection?.name}
      </Headline>
    ) : (
      <ul styleName="menu-items">
        {horizontalDefault?.map((menu, index) => {
          const { title, completeUrl, children } = menu;
          const hrefValue = ["link", "section", "tag"].includes(menu?.["item-type"]) ? completeUrl : "";
          return (
            <Fragment key={`${title}-${index}`}>
              {!children?.length ? (
                <HeaderMenuItem
                  isIconNeeded={false}
                  menuItemvalue={title}
                  href={completeUrl}
                  config={config}
                  story={story}
                />
              ) : (
                <li>
                  <MenuWithDropdown
                    subMenuitems={children}
                    menuItemvalue={title}
                    href={hrefValue}
                    config={config}
                    story={story}
                  />
                </li>
              )}
            </Fragment>
          );
        })}
      </ul>
    );

  /* NOTE: will remove bg-brand-primary for SignIn AuthButton only when Go_Ad_Free enabled */

  const defaultOrSecondaryHeaderBtns = !shouldUseStickyHeader ? (
    <>
      {/* <div styleName="btns">
        <AuthButton label={SIGN_IN} path="/user-login" customStyleName="" />
      </div>
      {enableSubscription && !isSubscriptionPage && !member && !isMobile && (
        <div styleName="go-ad-free-btn-top-bar-white">
          <Link socialButton href="/subscription">
            {GO_AD_FREE}
          </Link>
        </div>
      )} */}
    </>
  ) : (
    <div styleName="secondary-header-btns-wrapper">
      <div styleName="secondary-header-btns">
        {BUTTONS_DATA.map((item, index) => {
          const {
            iconType = "share",
            iconHeight = iconSize.height,
            iconWidth = iconSize.width,
            customStyleName = "secondary-header-btn",
            text = "",
            ariaLabel,
          } = item;
          if (iconType === "play-solid") {
            return null;
          }
          if (!shouldRenderSummaryButton && iconType === "summary") {
            return null;
          }
          if (iconType === "share") {
            return (
              <SocialShare
                popupText=""
                key={index}
                template={(props) => (
                  <SocialShareTemplate
                    {...props}
                    theme="rgb(0, 0, 0)"
                    story={story}
                    qtConfig={config}
                    onShareClick={(platform) => trackPopupSocialShareClick(platform?.toLowerCase())}
                  />
                )}
                buttonLabel=""
                fullUrl={encodeURI(story.url) !== "undefined" ? encodeURI(story.url) : encodeURI(shareURL)}
                title={
                  story.headline ||
                  collection?.name ||
                  qtState?.data?.query ||
                  qtState?.data?.title ||
                  qtState?.data?.author?.name ||
                  ""
                }
                shareIconId={`share-icon-story-share-${story.id}`}
              />
            );
          }

          if (pageType === "section-page" && index <= 1) {
            return null;
          }

          if (shouldRenderSummaryButton && iconType === "summary") {
            if (isSummaryEligibleStory) {
              const { category, subCategory, contentId } = getCategoryAndSubCategory(
                window.location,
                pageType,
                config,
                story
              );
              return (
                <span
                  key={`${index}-${iconType}`}
                  onClick={(e) => {
                    if (!isSummaryButtonEnabled) {
                      return;
                    }
                    // Push GA event
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                      event: "dl_story_interactions",
                      event_action: "summary_click",
                      category,
                      sub_category: subCategory,
                      content_id: contentId,
                      template_name: getTemplateName(pageType),
                      platform: window.innerWidth >= 768 ? "desktop" : "mobile",
                    });
                    setShowSummary(true);
                  }}
                >
                  <Button
                    iconType={iconType}
                    iconHeight={iconHeight}
                    iconWidth={iconWidth}
                    customStyleName={customStyleName}
                    ariaLabel={isSummaryButtonPending ? "Summary is loading" : ariaLabel}
                    disabled={!isSummaryButtonEnabled}
                  >
                    {text ? <span styleName="btn-text">{text}</span> : ""}
                  </Button>
                </span>
              );
            } else {
              return null;
            }
          }
          return (
            <span key={`${index}-${iconType}`}>
              <Button
                iconType={iconType}
                iconHeight={iconHeight}
                iconWidth={iconWidth}
                customStyleName={customStyleName}
                ariaLabel={ariaLabel}
              >
                {text ? <span styleName="btn-text">{text}</span> : ""}
              </Button>
            </span>
          );
        })}
      </div>
      {/* <Button customStyleName="bg-brand-primary">{GO_AD_FREE}</Button> */}
    </div>
  );
  return (
    <React.Fragment>
      {showOneTap && <OneTapLogin setMember={setMember} member={member} />}

      <div className="topbar-wrapper" styleName="topbar-wrapper">
        <div styleName={`topbar ${isAdfree ? "" : "margin-top-2px"}`}>
          <div styleName="left-wrapper">
            <div styleName="icons-wrapper">
              <div styleName="overlay" onClick={() => toggleHandler()} style={{ display: displayOverlay }}></div>
              <HamburgerMenu
                onMenuToggle={() => toggleHandler()}
                isMegaMenuOpen={isHamburgerMenuOpen}
                hamburgerMenu={hamburgerMenu}
              />

              <div styleName="applogo-wrapper" onClick={handleLogoClick}>
                <AppLogo customStyle="app-logo-header" />
              </div>
            </div>
            {headlineOrMenuItems}
            {shouldUseStickyHeader && isMobile && (
              <div onClick={handleLogoClick}>
                <AppLogo customStyle="app-logo-header" />
              </div>
            )}
          </div>
          {!shouldUseStickyHeader && isMobile && (
            <div styleName="middle-wrapper" onClick={handleLogoClick}>
              <AppLogo customStyle="app-logo-header" />
            </div>
          )}
          {/* <div styleName="right-wrapper" style={!storyHeaderInView ? { gap: "var(--spacing-m)" } : {}}> */}
          <div styleName="right-wrapper">
            {!isMobile && (<div styleName={`btn ${!shouldUseStickyHeader ? "active" : ""}`}>
              <NavbarSearch />
            </div>)}
             {!shouldUseStickyHeader && <Link href={`/open-offbeat-podcast`} disableAjaxLinks={true} styleName="offbeat-top-btn">OFFBEAT</Link>}
            {defaultOrSecondaryHeaderBtns}

            {/* {!isMobile && (
              <>
                {!shouldShow && show && (
                  <Link href={magazineBtnLink} styleName="subscribe-btn magazine-btn" disableAjaxLinks={true}>
                    <BookIcon /> {magazineBtnText}
                  </Link>
                )}
                {/* <Link href="/subscription" styleName="subscribe-btn" disableAjaxLinks={true}>
                  Go Ad-Free
                </Link>
              </>
            )} */}

            <div>
              {!shouldUseStickyHeader && (
                <div style={{cursor:'pointer'}} onClick={toggleThreeDotMenu}>
                  <ThreeDotIcon />
                </div>
              )}

              {showSubscribeButton && (
                <>
                <div styleName="subscribe-overlay" onClick={toggleThreeDotMenu}></div>
                <div styleName="mobile-subscribe-overlay" onClick={toggleThreeDotMenu}>
                  <div styleName="mobile-subscribe-buttons">
                    {show ? (
                      <Link href={magazineBtnLink} styleName="mobile-subscribe-button" disableAjaxLinks={true}>
                        <BookIcon color="#000000" /> {magazineBtnText}
                      </Link>
                    ) : null}
                    {/* <Link href="/user-login" styleName="sign-in mobile-subscribe-button" disableAjaxLinks={true}>
                      <SignInAvatar /> Sign In
                    </Link>} */}
                    {pageType !== "custom-event-page" && (
                      <AuthButton label={SIGN_IN} path="/user-login" isMobileTopbar onMenuToggle={onMenuToggle} />
                    )}
                    {enableSubscription && !isSubscriptionPage && !isAdfree && (
                      <Link
                        href="/subscription"
                        styleName="mobile-ad-free-button mobile-subscribe-button"
                        disableAjaxLinks={true}
                      >
                        Go Ad-Free
                      </Link>
                    )}
                  </div>
                </div>
                </>
              )}
            </div>
          </div>
        </div>
        {shouldUseStickyHeader && isMobile && pageType === "story-page" && story["story-template"] === "magazine" && (
          <Link href={readEmagazineLink} disableAjaxLinks={true}>
            <div styleName="read-e-magazine">
              Read Full E-Magazine
              <RightArrow color="#C6554F" />
            </div>
          </Link>
        )}
      </div>
      {shouldShowSummary && showSummary && (
        <SummaryCard
          setShowSummary={setShowSummary}
          story={story}
          summaryIndex={summaryIndex}
          summaryHtml={shouldUseApiSummary ? summary : ""}
          summaryTitle={shouldUseApiSummary ? apiHeadline : ""}
          summaryImage={shouldUseApiSummary ? apiHeroImageS3Key : ""}
        />
      )}
      {showSummary && <div styleName="blur-bg" />}
      {toastMessage && <ToastMessage message={toastMessage} onClose={() => setToastMessage("")} />}
    </React.Fragment>
  );
};

TopBar.propTypes = {
  showOneTap: bool,
  onMenuToggle: func,
};

export { TopBar };
